import type { Action } from 'components/Layout/PaymentViewLayout';
import type { InvoiceStatus } from 'payments-api/model';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import clsx from 'clsx';
import React from 'react';

import {
  kLabelColumnWidth,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';
import { StatusBadge } from '@fragment/ui/src/components/Payments/StatusBadge';

type Props = {
  onBack: () => void;
  title: string;
  primaryAction?: Action;
  secondaryAction?: Action;
  status: InvoiceStatus;
  issued: string;
  /** Total payin amount(s) rendered next to "Total" label (under Issued). */
  totalPayinContent?: React.ReactNode;
  setTitle?: (title: string) => void;
  children: React.ReactNode;
  historyPanel?: React.ReactNode;
};

export const InvoiceLayout = ({
  onBack,
  title,
  primaryAction,
  secondaryAction,
  status,
  issued,
  totalPayinContent,
  setTitle,
  children,
  historyPanel,
}: Props) => (
  <PaymentViewLayout
    title={title}
    onBack={onBack}
    primaryAction={primaryAction}
    secondaryAction={secondaryAction}
  >
    <div className="text-xmd font-helvetica flex flex-row w-full items-start">
      <div className="flex flex-col flex-1 min-w-0">
        {/* start title section */}
        <div className="flex w-full items-start border-b-[0.5px] border-main-200 h-f12">
          <TextCell
            text={title}
            textSize="lg"
            align="left"
            border="none"
            onChange={setTitle ? (text) => setTitle(text) : undefined}
            fullWidth
            data-testid="invoice-title-input"
          />
          <div className="flex flex-col h-full items-end justify-start p-f1 shrink-0 border-l-[0.5px] border-main-200">
            <div className="w-fit shrink-0">
              <StatusBadge status={status} />
            </div>
          </div>
        </div>

        {/* Issued row: same component pattern as Total Payins on user detail */}
        <div className="flex items-center border-b-[0.5px] border-main-200 w-full min-h-[1lh]">
          <div
            className={clsx(
              'px-f1 py-f1 text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0',
              kLabelColumnWidth
            )}
          >
            Issued
          </div>
          <div className="px-f1 py-f1 text-xmd text-main flex-1 font-helvetica">
            {new Date(issued).toDateString().split(' ').slice(1).join(' ')}
          </div>
        </div>

        {/* Total row: double height, upper-left justification */}
        <div className="flex items-start h-f8 w-full">
          <div
            className={clsx(
              'px-f1 pt-f1 text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full',
              kLabelColumnWidth
            )}
          >
            Total
          </div>
          <div className="px-f1 pt-f1 text-xmd text-main flex-1 flex flex-wrap items-start gap-x-f2 gap-y-f1">
            {totalPayinContent}
          </div>
        </div>
        {children}
      </div>
      {historyPanel && (
        <div className="text-xmd font-helvetica w-[400px] border-l border-main-200 min-h-full self-stretch">
          {historyPanel}
        </div>
      )}
    </div>
  </PaymentViewLayout>
);
